/**
 * PrintStationPage - Main print station interface
 *
 * Flow: Scan QR → View garment → Print (via agent) → Mark PRINTED in DB → Next
 *
 * Follows CatcherPage patterns:
 * - QRScannerInput for USB scanner capture
 * - GarmentCard-style display
 * - Hotkeys (capture phase)
 * - useScanFeedback for error modals
 * - useScannerAutoFocus for focus management
 */
import { useState, useCallback, useEffect, useRef, useMemo, useId } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PRINT_ERROR_META, formatOrderNumber, type PrinterFormat } from '@threadheads/shared';
import { useLocation } from '@/contexts/LocationContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { QRScannerInput } from '@/features/catcher-station';
import { useGarmentPrintLookup } from '../hooks/useGarmentPrintLookup';
import { useRecentlyPrinted } from '../hooks/useRecentlyPrinted';
import { usePrintAgent } from '../hooks/usePrintAgent';
import {
  PRINTER_FILE_TYPE,
  blockCopy,
  fileTypeMessage,
  isSideBlocked,
  printFailureCopy,
  type PrintSide,
  type PrinterFileCopy,
} from '../printer-file-type';
import { useScanFeedback } from '@/hooks/useScanFeedback';
import { useScannerAutoFocus } from '@/hooks/useScannerAutoFocus';
import { ScanErrorModal } from '@/components/ui/scan-error-modal';
import { SupersededStickerDialog } from '@/components/production/SupersededStickerDialog';
import {
  PrintTechnologyMismatchDialog,
  type PrintTechnologySwitchAttempt,
} from '@/components/production/PrintTechnologyMismatchDialog';
import { usePrintTechnologyMismatch } from '@/components/production/usePrintTechnologyMismatch';
import { PretreatWarning } from '@/components/production/PretreatWarning';
import { FilmSidesBadge } from '@/components/production/FilmSidesBadge';
import { ProductionImageLightbox } from '@/components/production/ProductionImageLightbox';
import {
  EMPTY_PRODUCTION_LIGHTBOX_DATA,
  productionPreviewKey,
} from '@/components/production/ProductionPreviewTile';
import type {
  ProductionPreviewFacts,
  ProductionPreviewKind,
} from '@/components/production/ProductionPreviewTile';
import { useProductionLightbox } from '@/components/production/useProductionLightbox';
import { PrintStationPreviews, buildPrintLightboxData } from './PrintStationPreviews';
import { cleanGarmentDisplayName } from '@/lib/garment-display-name';
import { PrintIssueDialog, PRINT_FILE_REASONS } from './PrintIssueDialog';
import { GarmentIssueDialog, type GarmentIssueReason } from './GarmentIssueDialog';
import { TestPrintFileDialog } from './TestPrintFileDialog';
import { ResolvedIssuesPanel } from './ResolvedIssuesPanel';
import { useFormatTimeAgo } from '../hooks/useFormatTimeAgo';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { getUserMessage, isSupplySourceUnreadable, parseRedoError, parsePapBlocked, parsePendingLabels, parseOrderOnHold, parseSupersededError } from '@/lib/trpc-error-utils';
import { playErrorBeep, playSuccessBeep } from '@/lib/sounds';
import { detectQRType, encodeGarmentQr, extractGarmentId } from '@/lib/qr-utils';
import { useTechAwareScanRouting } from '@/features/production-orders/hooks/useTechAwareScanRouting';
import { parseDtfProductCause } from '@/lib/trpc-error-utils';
import { SCAN_ROUTE_GARMENT_PARAM } from '@/features/production-orders/utils/print-tech-routing';
import { PRINT_TECHNOLOGY_SWITCH_REASON } from '@/features/production-tracker/lib/print-technology-switch';
import { CLIENT_QC_REDO_REASON } from '@/features/production-tracker/lib/client-qc-redo';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useBackendHeartbeat } from '@/hooks/useBackendHeartbeat';
import { ConnectionStatusBanner } from '@/components/ui/connection-status-banner';
import {
  Printer,
  ArrowLeft,
  CheckCircle,
  WifiOff,
  ExternalLink,
  RefreshCw,
  Clock,
  AlertTriangle,
  AlertCircle,
  Layers,
  Shirt,
  Loader2,
  FlaskConical,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useHotkeyEngine } from '@/features/production-settings/hotkeys/useHotkeyEngine';
import { useHotkeySettings } from '@/features/production-settings/hotkeys/useHotkeySettings';
import type { HotkeyAction } from '@threadheads/shared';
import { formatBindingLabel } from '@/features/production-settings/hotkeys/hotkey-types';
import { ZoomImg } from '@/components/production/HoverZoom';

/**
 * BLU-435: 4-state matrix derived from HEAD-verified hasFront/hasBack on the
 * lookup response. Drives the indicator banner palette/copy, the sticky-bottom
 * button branch, and the hotkey routing.
 */
type SideState = 'dual' | 'frontOnly' | 'backOnly' | 'notReady';

export function PrintStationPage() {
  const { t } = useTranslation('production');
  const { selectedLocation } = useLocation();
  const { organization } = useOrganization();
  const heartbeat = useBackendHeartbeat();
  const feedback = useScanFeedback();
  const formatTimeAgo = useFormatTimeAgo();

  // Tech-aware scan routing (BLU-233): a DTF garment scanned here is never
  // printed blind. This is the DTG station, so stationTech='DTG'. Only a
  // DB-CONFIRMED tech decides (2026-08-08 incident: the scanned label's `m`
  // can go stale once a unit's DB print_technology is corrected after the
  // label was printed); getGarmentForPrint's own DTF_PRODUCT guard, which
  // reads the DB, is the signal. BLU-1509: it opens the mismatch dialog
  // (Go to film station / Print it DTG instead) instead of bouncing at once.
  const routing = useTechAwareScanRouting('DTG', feedback);
  const { bounceToTech } = routing;
  const mismatch = usePrintTechnologyMismatch('DTF', routing);
  const { openMismatch, close: closeMismatch, closeSwitched } = mismatch;
  // Garment id of the last scan: the shirt the mismatch dialog switches, and
  // the ?garment=<id> carried to the film station.
  const lastScannedGarmentIdRef = useRef<string | null>(null);
  // BLU-1509: the shirt just switched to DTG here (matched by id), for its
  // pretreat banner and its missing DTG file report.
  const [flippedShirt, setFlippedShirt] = useState<{ garmentId: string; needsPretreat: boolean } | null>(null);
  const reportedFlipFileRef = useRef<string | null>(null);

  // Incoming ?garment=<id> context from a cross-tech redirect off the Film-Print
  // station: pre-select that garment by resolving it as if it were scanned.
  const [searchParams, setSearchParams] = useSearchParams();

  // Garment lookup
  const {
    data: garmentData,
    error: lookupError,
    isLoading: isLookingUp,
    lookup: lookupGarment,
    clear: clearGarment,
  } = useGarmentPrintLookup();

  // Single choke point for every lookupGarment call: extracts + records the
  // scanned garment id AT THE POINT OF LOOKUP, not just in handleScan's raw
  // QR path. Without this, the ?garment= deep-link resolve, the resolved-
  // issues panel reload, and the "Recently Printed" re-lookup all called
  // lookupGarment directly and left lastScannedGarmentIdRef stale/null — a
  // DTF_PRODUCT rejection on any of those paths would then bounce with the
  // wrong (or no) garment id.
  const lookupGarmentById = useCallback((qrCode: string) => {
    lastScannedGarmentIdRef.current = extractGarmentId(qrCode);
    lookupGarment(qrCode);
  }, [lookupGarment]);

  // Print agent connectivity
  const {
    isConnected: agentConnected,
    isLoading: agentProbing,
    isPrinting,
    print: printViaAgent,
    printerFormat,
    confirmPrinterFormat,
  } = usePrintAgent();
  // BLU-1336: `printerFormat` is this PC's declared printer format. Display
  // and guard evidence only.
  const showPrinterFileType = !agentProbing && agentConnected;
  // The format the guard judges by: an offline agent never stops a print
  // (handlePrint already refuses with the not-connected warning).
  const stationFormat: PrinterFormat | null = agentConnected ? printerFormat : null;

  // Recently printed (idle state)
  const { data: recentlyPrinted } = useRecentlyPrinted(selectedLocation?.id ?? null);

  // Mark printed mutations
  const markPrintedMutation = trpc.printStation.markPrinted.useMutation();
  const markDualSideMutation = trpc.printStation.markDualSidePrinted.useMutation();

  // Dual-print side tracking (local state updated after each side is printed),
  // tied to the garment unit it was read for: see frontPrintedAt below.
  const [sideTimes, setSideTimes] = useState<{
    unitId: string;
    front: string | null;
    back: string | null;
  } | null>(null);

  // Dual-print reprint confirmation
  const [reprintConfirmSide, setReprintConfirmSide] = useState<'front' | 'back' | null>(null);
  // BLU-1287: what the confirm says, frozen when it opens, so its title,
  // description and buttons do not flip during the exit animation (after a
  // mark the garment clears and nothing is "not marked as printed" any more).
  const [confirmView, setConfirmView] = useState<{ side: 'front' | 'back'; notDone: boolean }>({ side: 'front', notDone: false });

  // "Test a Print File" debug downloader — always-available trigger in the header.
  const [testFileOpen, setTestFileOpen] = useState(false);

  // Issue reporting
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const reportIssueMutation = trpc.productionOrders.issues.create.useMutation();
  // "Missing Artwork" → raise an uploadable file_missing PRINT_FILE_ISSUE so the
  // Issues → Print Files uploader can re-upload the resolved file.
  const reportPrintFileMissingMutation = trpc.printStation.reportPrintFileMissing.useMutation();
  const reportPrintFileMissing = reportPrintFileMissingMutation.mutateAsync;
  // BLU-1509: the safe in-place DTF to DTG flip (the server refuses anything else).
  const switchToDtgMutation = trpc.printStation.switchToDtg.useMutation();
  const switchToDtg = switchToDtgMutation.mutateAsync;

  // Garment issue (contamination / manufacturer defect / wrong item)
  const [garmentIssueDialogOpen, setGarmentIssueDialogOpen] = useState(false);
  const reportGarmentIssueMutation = trpc.printStation.reportGarmentIssue.useMutation();

  // Configured print file issue reasons
  const printFileReasonsQuery = trpc.productionSettings.getPrintFileIssueReasons.useQuery();
  const printFileReasons = printFileReasonsQuery.data?.reasons ?? PRINT_FILE_REASONS;

  // Last printed item info (shown briefly after print)
  const [lastPrintedInfo, setLastPrintedInfo] = useState<{
    id: string;
    productName: string;
    size: string | null;
    color: string | null;
    imageUrl: string | null;
  } | null>(null);
  const lastPrintedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Error tracking (same pattern as catcher)
  const hasScannedRef = useRef(false);
  const shownErrorRef = useRef<string | null>(null);

  // BLU-435: HEAD-verified 4-state matrix derived from authoritative hasFront/hasBack.
  // Stable identity for downstream useMemo deps (hotkey memo). Single source of truth
  // for indicator banner + button branch + hotkey routing.
  const sideState: SideState | null = useMemo(() => {
    if (!garmentData) return null;
    if (garmentData.hasFront && garmentData.hasBack) return 'dual';
    if (garmentData.hasFront) return 'frontOnly';
    if (garmentData.hasBack) return 'backOnly';
    return 'notReady';
  }, [garmentData]);

  // BLU-1018 — DISPLAY PREVIEW FACTS. Read-only projection of the lookup
  // response for the card's two preview groups. `hasFront`/`hasBack` are the
  // AUTHORITATIVE HEAD-verified flags read straight off the response and used
  // here only to decide which preview SLOTS exist; nothing in this block
  // writes back to them, to `sideState`, or to any printable path.
  const previewFacts = useMemo<ProductionPreviewFacts | null>(() => {
    if (!garmentData) return null;
    const unit = garmentData.garmentUnit;
    return {
      imageUrl: unit.imageUrl,
      customPreviewUrl: unit.customPreviewUrl,
      customPreviewSourceUrl: unit.customPreviewSourceUrl,
      frontMockupPreviewUrl: unit.frontMockupPreviewUrl,
      backMockupPreviewUrl: unit.backMockupPreviewUrl,
      frontPreviewUrl: unit.frontPreviewUrl,
      backPreviewUrl: unit.backPreviewUrl,
      hasFront: garmentData.hasFront,
      hasBack: garmentData.hasBack,
      isCustomProduct: unit.isCustomProduct,
    };
  }, [garmentData]);

  // Garment-wide image lightbox. Declared with the other focus-trapping
  // overlays because the scanner/hotkey pause lists below read it.
  //
  // ONE FLAT LIST for the garment on screen, built by the SAME builder the
  // card renders from, so a click opens that exact image and the arrows ride
  // this garment's other side. The overlay's state (the clicked tile's
  // fallthrough, the empty-list guard, the reset on a new garment) is
  // `useProductionLightbox`, shared with the other stations.
  const lightboxData = useMemo(
    () =>
      garmentData && previewFacts
        ? buildPrintLightboxData({
            garmentUnitId: garmentData.garmentUnit.id,
            subject: cleanGarmentDisplayName(
              garmentData.garmentUnit.productName,
              garmentData.garmentUnit.color,
              garmentData.garmentUnit.size,
            ),
            facts: previewFacts,
          })
        : EMPTY_PRODUCTION_LIGHTBOX_DATA,
    [garmentData, previewFacts],
  );
  const lightbox = useProductionLightbox({
    entries: lightboxData.entries,
    indexByKey: lightboxData.indexByKey,
    resetKey: garmentData?.garmentUnit.id,
  });
  const openLightboxAt = lightbox.openAt;

  const handleOpenPreview = useCallback(
    (kind: ProductionPreviewKind, sourceIndex: number) => {
      if (!garmentData) return;
      openLightboxAt(productionPreviewKey(garmentData.garmentUnit.id, kind), sourceIndex);
    },
    [openLightboxAt, garmentData],
  );

  // Sync per-side printed timestamps from lookup response (MED-2 widened to
  // cover back-only / front-only so re-scanned partially-printed garments
  // preserve their already-done UI state).
  useEffect(() => {
    if (garmentData?.hasFront || garmentData?.hasBack) {
      setSideTimes({
        unitId: garmentData.garmentUnit.id,
        front: garmentData.garmentUnit.frontPrintedAt,
        back: garmentData.garmentUnit.backPrintedAt,
      });
    } else {
      setSideTimes(null);
    }
  }, [garmentData]);
  // The effect above lands one commit after a new scan, so for that commit
  // `sideTimes` still holds the previous garment's times. Read them only
  // when they belong to the garment on screen; otherwise use this lookup's
  // own times. Everything that picks Mark vs Print (requiredSidesPrinted,
  // the hotkeys, the main button, the reprint confirm) reads these, so a
  // fresh garment scanned right after a stamped one is never marked.
  const hasSides = !!garmentData && (garmentData.hasFront || garmentData.hasBack);
  const ownSideTimes =
    hasSides && sideTimes?.unitId === garmentData.garmentUnit.id ? sideTimes : null;
  const frontPrintedAt = !hasSides
    ? null
    : ownSideTimes
      ? ownSideTimes.front
      : garmentData.garmentUnit.frontPrintedAt;
  const backPrintedAt = !hasSides
    ? null
    : ownSideTimes
      ? ownSideTimes.back
      : garmentData.garmentUnit.backPrintedAt;

  // BLU-1287 B2: every side this garment needs already has a per-side print
  // time, yet it is not PRINTED (e.g. half-printed before its SKU was
  // confirmed Front only). One status line says so, "Mark as printed" is the
  // main button and Reprint is secondary (its confirm also offers the mark).
  // Derived per render from the lookup and the page's own per-side state.
  const requiredSidesPrinted =
    !!garmentData &&
    !garmentData.alreadyPrinted &&
    (garmentData.hasFront || garmentData.hasBack) &&
    (!garmentData.hasFront || !!frontPrintedAt) &&
    (!garmentData.hasBack || !!backPrintedAt);
  // The side Mark as printed stamps (the main button and the print hotkey).
  const markSide: 'front' | 'back' = garmentData?.hasFront ? 'front' : 'back';

  // BLU-1336: the applicable sides this PC's printer cannot print, from THIS
  // scan's own file evidence (printer-file-type.ts rule 1). Derived per render;
  // never feeds hasFront / hasBack. `blockedSides` drives the press guard, so
  // a reprint of a blocked side is still refused when pressed.
  // `blockedToPrint` drops sides already printed (and every side of a
  // garment already PRINTED), so a printed side never raises a scan-time stop,
  // a red pill or a disabled Reprint.
  const blockedSides = useMemo<readonly PrintSide[]>(() => {
    if (!garmentData || stationFormat === null) return [];
    const lanes = garmentData.printFileLanes;
    const sides: PrintSide[] = [];
    if (garmentData.hasFront && isSideBlocked(stationFormat, lanes?.front ?? null)) sides.push('front');
    if (garmentData.hasBack && isSideBlocked(stationFormat, lanes?.back ?? null)) sides.push('back');
    return sides;
  }, [garmentData, stationFormat]);
  const blockedToPrint: readonly PrintSide[] = garmentData?.alreadyPrinted
    ? []
    : blockedSides.filter((side) => !(side === 'front' ? frontPrintedAt : backPrintedAt));
  const blockedToPrintKey = blockedToPrint.join(',');
  const frontBlocked = blockedToPrint.includes('front');
  const backBlocked = blockedToPrint.includes('back');
  // Every side still to print is blocked: nothing on this garment can be
  // printed here. A Brother gets Retry Scan as the main action (RIP lag); a
  // Kora gets no main Print button and a larger pill.
  const remainingSides =
    Number(!!garmentData?.hasFront && !frontPrintedAt) + Number(!!garmentData?.hasBack && !backPrintedAt);
  const hideBlockedPrint = blockedToPrint.length > 0 && blockedToPrint.length >= remainingSides;
  const showFileTypeRetry = hideBlockedPrint && stationFormat === 'arx4';
  const fileTypePillId = useId();
  const fileTypePillShown = blockedToPrint.length > 0;

  // BLU-1336: one renderer for every printer file-type message (scan-time
  // modal, pill, refused print, print-time failure). The key choice is the
  // pure `fileTypeMessage`; this only translates.
  const fileTypeText = useCallback((copy: PrinterFileCopy, sides: readonly PrintSide[]) => {
    const applicable = Number(!!garmentData?.hasFront) + Number(!!garmentData?.hasBack);
    const msg = fileTypeMessage(copy, sides, applicable);
    const params = msg.sideKey ? { ...msg.params, side: t(msg.sideKey) } : msg.params;
    return { title: t(msg.titleKey, params), body: t(msg.bodyKey, params) };
  }, [garmentData?.hasFront, garmentData?.hasBack, t]);

  // BLU-1336: the one stop. Given the declaration a fresh /status read just
  // confirmed, shows the red modal (with buzz) for the sides it still blocks.
  // Returns true when it stopped. A null answer (unknown) or a format that no
  // longer blocks lets the print proceed.
  const stopIfBlocked = useCallback((
    confirmed: PrinterFormat | null,
    sides: readonly PrintSide[],
  ): boolean => {
    if (confirmed === null) return false;
    const lanes = garmentData?.printFileLanes ?? null;
    const stopped = sides.filter((side) => isSideBlocked(confirmed, lanes?.[side] ?? null));
    if (stopped.length === 0) return false;
    const { title, body } = fileTypeText(blockCopy(confirmed), stopped);
    feedback.triggerError(title, body);
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `feedback` object is intentionally not memoized (memory feedback_use_scan_feedback_unstable).
  }, [garmentData, fileTypeText]);

  // BLU-1336: scan-time stop, once per (lookup result, format, blocked sides),
  // only when the confirm returns the same declaration. Never for a printed
  // side or a PRINTED garment, and held while another overlay owns the
  // screen (the pill still shows; the stop fires once it closes). The ref is
  // NOT reset when nothing is blocked, so a brief null format never re-arms it.
  const fileTypeOverlayOpen = issueDialogOpen || garmentIssueDialogOpen || lightbox.open || testFileOpen || mismatch.open;
  const fileTypeStopKeyRef = useRef<{ lookup: unknown; format: PrinterFormat; sides: string } | null>(null);
  useEffect(() => {
    if (!garmentData || garmentData.alreadyPrinted || stationFormat === null || blockedToPrintKey === '') return;
    if (fileTypeOverlayOpen) return;
    const last = fileTypeStopKeyRef.current;
    if (last && last.lookup === garmentData && last.format === stationFormat && last.sides === blockedToPrintKey) return;
    const key = { lookup: garmentData, format: stationFormat, sides: blockedToPrintKey };
    fileTypeStopKeyRef.current = key;
    const format = stationFormat;
    let cancelled = false;
    let settled = false;
    const sides = blockedToPrintKey.split(',') as PrintSide[];
    void confirmPrinterFormat().then((confirmed) => {
      settled = true;
      // A different answer re-renders under a new key; null is unknown. Neither stops.
      if (!cancelled && confirmed === format) stopIfBlocked(confirmed, sides);
    });
    return () => {
      cancelled = true;
      // StrictMode re-runs effects at mount: a confirm cut off before it
      // answered must re-arm, or the stop would never fire.
      if (!settled && fileTypeStopKeyRef.current === key) fileTypeStopKeyRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `stopIfBlocked` is read from this render (it carries the unmemoized `feedback`), so a re-render can never cancel an in-flight confirm.
  }, [garmentData, stationFormat, blockedToPrintKey, fileTypeOverlayOpen, confirmPrinterFormat]);

  // BLU-1336: the print guard for one side, printed or not. Unblocked sides
  // pass at once (no probe). A second press during the confirm is dropped.
  const fileTypeGuardRef = useRef(false);
  const refuseFileTypeBlocked = useCallback(async (side: PrintSide): Promise<boolean> => {
    // Backstop: both callers already skip an unblocked side before the await.
    if (!blockedSides.includes(side)) return false;
    if (fileTypeGuardRef.current) return true;
    fileTypeGuardRef.current = true;
    try {
      return stopIfBlocked(await confirmPrinterFormat(), [side]);
    } finally {
      fileTypeGuardRef.current = false;
    }
  }, [blockedSides, confirmPrinterFormat, stopIfBlocked]);

  // Audio twin for Not-ready state (HIGH-6): fires once per garment scan via
  // useScanFeedback.triggerError. Visual + audio twin pair per PRODUCTION_DESIGN.md §5.2.
  // BLU-1509: a shirt just switched to DTG never had its DTG file made, so
  // report it once (the bridge then makes it) and say so; if the report
  // fails, the generic modal, whose Report Print Issue still works.
  const notReadyFiredRef = useRef<string | null>(null);
  const notReadyGarmentId = garmentData?.garmentUnit.id ?? null;
  const notReadyLocationId = selectedLocation?.id ?? null;
  useEffect(() => {
    if (sideState === 'notReady' && notReadyGarmentId && notReadyFiredRef.current !== notReadyGarmentId) {
      notReadyFiredRef.current = notReadyGarmentId;
      const showGenericNotReady = () =>
        feedback.triggerError(t('printer.filesNotReadyTitle'), t('printer.filesNotReadyBanner'));
      const showBeingMade = () =>
        feedback.triggerWarning(
          t('scanRouting.switch.fileBeingMadeTitle'),
          t('scanRouting.switch.fileBeingMadeBody'),
        );
      if (flippedShirt?.garmentId !== notReadyGarmentId || !notReadyLocationId) {
        showGenericNotReady();
      } else if (reportedFlipFileRef.current === notReadyGarmentId) {
        showBeingMade();
      } else {
        reportedFlipFileRef.current = notReadyGarmentId;
        const reportedId = notReadyGarmentId;
        // The answer can land after the operator cleared or scanned on:
        // speak only while this not-ready shirt is still the one up.
        const stillUp = () => notReadyFiredRef.current === reportedId;
        void reportPrintFileMissing({
          garmentUnitId: reportedId,
          locationId: notReadyLocationId,
          reason: 'missing_artwork',
          extra: { failure_stage: 'print_technology_flip' },
        }).then(
          () => { if (stillUp()) showBeingMade(); },
          () => {
            reportedFlipFileRef.current = null;
            if (stillUp()) showGenericNotReady();
          },
        );
      }
    }
    if (sideState !== 'notReady') {
      // Reset when leaving notReady so a future re-scan of same garment fires again.
      notReadyFiredRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `feedback` object is intentionally not memoized; see memory feedback_use_scan_feedback_unstable
  }, [sideState, notReadyGarmentId, notReadyLocationId, flippedShirt, reportPrintFileMissing, t]);

  // BLU-1509: the pretreat banner's one buzz (§5.2), once per shirt; a
  // not-ready shirt's own modal already buzzes.
  const showFlipPretreat =
    !!garmentData && flippedShirt?.garmentId === garmentData.garmentUnit.id && flippedShirt.needsPretreat;
  const pretreatBuzzRef = useRef<string | null>(null);
  useEffect(() => {
    if (!showFlipPretreat || !notReadyGarmentId || sideState === 'notReady') return;
    if (pretreatBuzzRef.current === notReadyGarmentId) return;
    pretreatBuzzRef.current = notReadyGarmentId;
    playErrorBeep();
  }, [showFlipPretreat, notReadyGarmentId, sideState]);

  // Clear state on location change
  useEffect(() => {
    clearGarment();
    setIssueDialogOpen(false);
    setGarmentIssueDialogOpen(false);
    closeMismatch();
    setFlippedShirt(null);
    hasScannedRef.current = false;
    shownErrorRef.current = null;
  }, [selectedLocation?.id, clearGarment, closeMismatch]);

  // BLU-233: consume the ?garment=<id> context handed over by a cross-tech
  // redirect from the Film-Print station. Resolve it as if scanned (same QR
  // shape the "Recently Printed" rows use), then strip the param so a refresh /
  // back-nav doesn't re-trigger. Guards on a non-empty location + a once-per-id
  // ref so the lookup fires exactly once. If the garment can't be resolved the
  // lookup's own error path surfaces a modal — no crash, lands cleanly.
  const consumedGarmentParamRef = useRef<string | null>(null);
  useEffect(() => {
    const garmentParam = searchParams.get(SCAN_ROUTE_GARMENT_PARAM);
    if (!garmentParam) return;
    if (!selectedLocation?.id) return; // wait for a location before resolving
    if (consumedGarmentParamRef.current === garmentParam) return;
    consumedGarmentParamRef.current = garmentParam;

    hasScannedRef.current = true;
    lookupGarmentById(encodeGarmentQr(garmentParam));

    // Strip the param so it doesn't re-fire on refresh / back-nav.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(SCAN_ROUTE_GARMENT_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [searchParams, selectedLocation?.id, lookupGarmentById, setSearchParams]);

  // Show error modal on lookup failure
  useEffect(() => {
    if (!hasScannedRef.current) return;
    if (lookupError) {
      if (shownErrorRef.current === lookupError) return;
      shownErrorRef.current = lookupError;

      // BLU-233 + 2026-08-08 DB-trust fix: getGarmentForPrint's own
      // DTF_PRODUCT guard reads the garment row's print_technology — the DB,
      // never the scanned label — and is the ONLY signal that this shirt
      // belongs on the film station. Checked first so a DB-confirmed
      // cross-tech scan never falls through to the generic scanError copy.
      // BLU-1509: ask (go to the film station, or print it DTG here).
      const dtfProduct = parseDtfProductCause(lookupError);
      if (dtfProduct.isDtfProduct) {
        const garmentId = lastScannedGarmentIdRef.current;
        if (garmentId) openMismatch(garmentId, dtfProduct.needsPretreat);
        else bounceToTech('DTF', null);
        return;
      }

      const pending = parsePendingLabels(lookupError);
      const parsed = parseRedoError(lookupError);
      const onHold = parseOrderOnHold(lookupError);
      const superseded = parseSupersededError(lookupError);
      if (superseded.isSuperseded) {
        // Sticker invalidated by a re-batch — shirt is fine; offer the new sticker (amber).
        feedback.triggerSuperseded(superseded.replacement);
      } else if (onHold.isOnHold) {
        // Order paused (reversible) — amber, set aside; checked before the print-specific blocks.
        feedback.triggerOrderOnHold(onHold.holdReason);
      } else if (parsePapBlocked(lookupError)) {
        // PAP product — pre-decorated, must never print (red, hard block).
        feedback.triggerPapBlocked();
      } else if (pending.isPendingLabels) {
        // Batch made but QR labels not printed yet — guide to the Picklist menu.
        feedback.triggerPendingLabels(pending.batchNumber);
      } else if (parsed.isRedoError) {
        feedback.triggerRedoBlocked(parsed.replacement);
      } else if (parsed.isInventoryError) {
        // The order was pulled for missing stock: the blank goes back to stock,
        // never to the printer. The catcher's own amber modal and copy.
        feedback.triggerStockIssue();
      } else if (parsed.isOrderItemRemoved) {
        feedback.triggerOrderItemRemoved();
      } else if (parsed.isOrderCancelled) {
        feedback.triggerOrderCancelled();
      } else {
        feedback.triggerError(t('scanError'), getUserMessage(lookupError));
      }
    } else {
      shownErrorRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupError]);

  // Note: loading state is shown inline (loading card) instead of a toast

  // Auto-focus scanner. FRONTEND-4N: do NOT pause for isPrinting/isLookingUp —
  // a scan during/right after printing or lookup must still land in the scanner
  // input (it is key-ahead-buffered by QRScannerInput), never on document.body
  // where it can end up appended to the URL → 404. Pause only for true
  // modal/dialog states where a scan must not be consumed (mirrors CatcherPage;
  // the hotkey engine below keeps its own isPrinting guard — that one prevents
  // double prints and is unrelated to focus).
  useScannerAutoFocus({
    paused: feedback.isModalOpen || mismatch.open || issueDialogOpen || garmentIssueDialogOpen || testFileOpen || !!reprintConfirmSide || lightbox.open,
  });

  // Last structured print failure — fed into Report Print Issue so the report
  // carries the exact code + correlation id (a dev can then trace it in
  // Sentry/Railway without reading the operator's local log).
  const lastPrintFailureRef = useRef<{
    code?: string;
    correlationId?: string;
    stage?: string;
    httpStatus?: number;
  } | null>(null);

  // After a per-side mark (markDualSidePrinted): sync the per-side times, beep,
  // and on PRINTED show the last-printed card and clear the garment. Shared by
  // the per-side print path and the reprint confirm's Mark as printed.
  const applyDualSideMark = useCallback((result: {
    frontPrintedAt: string | null;
    backPrintedAt: string | null;
    newStatus: string;
  }) => {
    if (!garmentData) return;
    // Update local side state
    setSideTimes({
      unitId: garmentData.garmentUnit.id,
      front: result.frontPrintedAt,
      back: result.backPrintedAt,
    });

    playSuccessBeep();

    // MED-11: clear when the DAO declares terminal status — works for
    // back-only, front-only, AND dual uniformly via the allRequiredDone
    // formula in Plan-01 DAO.
    if (result.newStatus === 'PRINTED') {
      setLastPrintedInfo({
        id: garmentData.garmentUnit.id,
        productName: garmentData.garmentUnit.productName,
        size: garmentData.garmentUnit.size,
        color: garmentData.garmentUnit.color,
        imageUrl: garmentData.garmentUnit.imageUrl,
      });
      if (lastPrintedTimerRef.current) clearTimeout(lastPrintedTimerRef.current);
      lastPrintedTimerRef.current = setTimeout(() => setLastPrintedInfo(null), 15000);
      clearGarment();
    }
    // Otherwise keep garment on screen for the other side
  }, [garmentData, clearGarment]);

  // Handle print + mark printed in DB
  const handlePrint = useCallback(async (dualSide?: 'front' | 'back') => {
    if (!garmentData || !selectedLocation?.id) return;
    // The side this call prints. Side-less calls exist only in the front-only state.
    const side = dualSide ?? 'front';

    // Print-status integrity: a garment is marked PRINTED only after the agent
    // confirms it fetched + sent the file. With no agent connected nothing was
    // sent, so never fall through to markPrinted below. This also closes the
    // `printer_print` hotkey hole — unlike the on-screen Print buttons (hidden
    // when disconnected), the hotkey isn't gated on connection.
    if (!agentConnected) {
      feedback.triggerWarning(
        t('printer.printAgentNotConnectedTitle'),
        t('printer.printAgentUnreachableBody'),
      );
      return;
    }

    // BLU-1336: this side's file is proven missing for this printer and present
    // for the other one; refuse before the agent is called. The sync check
    // keeps an unblocked print's timing exactly as today (no extra await).
    if (blockedSides.includes(side) && await refuseFileTypeBlocked(side)) return;

    // Connection guaranteed by the guard above. Bare block scopes `result` so
    // it doesn't collide with the dual-side mutation's `result` further down.
    {
      const result = await printViaAgent({
        sku: garmentData.garmentUnit.shopifySku || garmentData.garmentUnit.sku,
        // BLU-1287 B1: side-less calls exist only in the front-only state.
        // Naming the side makes every agent (including the legacy-azure
        // fallback at apps/print-agent/src/index.ts:768, which serves the
        // back of a two-sided record for a side-less request) fetch the front.
        // `dualSide` itself still decides the completion writer below.
        dualSide: side,
        artworkBarcode: garmentData.garmentUnit.artworkBarcode ?? undefined,
        // BLU-368: opt into the backend-mediated routing path. The agent
        // forwards locationId to /api/print-agent/get-print-file so multi-
        // tenant + Tencent COS locations resolve correctly. Older agents
        // without BACKEND_URL configured ignore the field and fall back to
        // their existing local Azure-Tables lookup.
        locationId: selectedLocation.id,
      });

      if (!result.success) {
        // Remember the structured failure so a follow-up "Report Print Issue"
        // carries the exact code + correlation id to the backend.
        lastPrintFailureRef.current = {
          code: result.code,
          correlationId: result.correlationId,
          stage: result.stage,
          httpStatus: result.httpStatus,
        };

        // BLU-1336 (D-04): never say "not uploaded" when the other printer's
        // file may exist. Null keeps today's copy below. No confirm here: the
        // failure is a fact about this attempt, not a stop.
        const fileTypeCopy = printFailureCopy(
          stationFormat,
          garmentData.printFileLanes?.[side] ?? null,
          {
            code: result.code,
            stage: result.stage,
            httpStatus: result.httpStatus,
            detailCode: result.detailCode,
          },
        );
        if (fileTypeCopy) {
          const { title, body } = fileTypeText(fileTypeCopy, [side]);
          const ref = result.correlationId ? result.correlationId.slice(0, 8) : null;
          feedback.triggerError(title, body, ref ? t('printer.errorRef', { ref }) : undefined);
          return;
        }

        // Clearer-errors: branch the failure UX on the agent's specific code via
        // the shared registry. Branch lives inline in this callback (not a
        // useEffect) to sidestep the unstable-`feedback` deps footgun
        // (PRODUCTION_DESIGN.md §3.2).
        const meta =
          result.code && result.code !== 'unknown' ? PRINT_ERROR_META[result.code] : undefined;
        if (meta) {
          const bodyText = t(`${meta.i18nKey}Body`);
          const ref = result.correlationId ? result.correlationId.slice(0, 8) : null;
          // Show a short ref the operator can quote to a dev; the full corr id
          // rides along on any reported issue + the backend beacon.
          const body = ref ? `${bodyText}\n\n${t('printer.errorRef', { ref })}` : bodyText;
          // severity → red (operator must flag / escalate) vs amber (re-tappable).
          if (meta.severity === 'terminal' || meta.severity === 'config') {
            feedback.triggerError(t(`${meta.i18nKey}Title`), body);
          } else {
            feedback.triggerWarning(t(`${meta.i18nKey}Title`), body);
          }
          return;
        }

        // Disk-full fallback for agents that don't yet emit the `disk_full` code
        // (≤ v1.6.0): the raw ENOSPC error rides along in `message`. Match it so
        // the operator gets the clear "free up disk space" copy now, before the
        // agent auto-updates to send the code. (Newer agents hit the `meta`
        // branch above with code='disk_full' and never reach here.)
        if (result.reason === 'disk_full' || /ENOSPC|no space left/i.test(result.message ?? '')) {
          feedback.triggerError(t('printer.errDiskFullTitle'), t('printer.errDiskFullBody'));
          return;
        }

        // Fallback — older agents send only a coarse `reason` (no code).
        if (result.reason === 'agent_unreachable') {
          feedback.triggerWarning(
            t('printer.printFailedRetryTitle'),
            t('printer.printAgentUnreachableBody'),
          );
        } else if (result.reason === 'print_open_failed') {
          feedback.triggerWarning(
            t('printer.printFailedOpenTitle'),
            t('printer.printFailedOpenBody'),
          );
        } else if (result.retryable === false) {
          if (result.reason === 'credentials') {
            feedback.triggerError(
              t('printer.printFailedCredentialsTitle'),
              t('printer.printFailedCredentialsBody'),
            );
          } else if (result.reason === 'artwork_missing') {
            feedback.triggerError(
              t('printer.printFailedMissingTitle'),
              t('printer.printFailedMissingBody'),
            );
          } else {
            feedback.triggerError(
              t('printer.printFailedTerminalTitle'),
              t('printer.printFailedTerminalBody'),
            );
          }
        } else {
          feedback.triggerWarning(
            t('printer.printFailedRetryTitle'),
            t('printer.printFailedRetryBody'),
          );
        }
        return;
      }
    }

    // Mark as printed in DB
    try {
      if (dualSide) {
        // BLU-435 CRIT-1: Route ALL side-specific prints (back-only, front-only,
        // dual) through markDualSidePrinted. The mutation honours
        // requiredFront/requiredBack and writes status=PRINTED when all required
        // sides are timestamped (allRequiredDone). Without this widening,
        // back-only PW customs stayed PICKED forever.
        const result = await markDualSideMutation.mutateAsync({
          garmentUnitId: garmentData.garmentUnit.id,
          locationId: selectedLocation.id,
          side: dualSide,
          requiredFront: garmentData.hasFront,
          requiredBack: garmentData.hasBack,
        });
        applyDualSideMark(result);
      } else {
        // Single print: mark fully printed
        await markPrintedMutation.mutateAsync({
          garmentUnitId: garmentData.garmentUnit.id,
          locationId: selectedLocation.id,
        });

        playSuccessBeep();

        // Capture printed item info before clearing
        setLastPrintedInfo({
          id: garmentData.garmentUnit.id,
          productName: garmentData.garmentUnit.productName,
          size: garmentData.garmentUnit.size,
          color: garmentData.garmentUnit.color,
          imageUrl: garmentData.garmentUnit.imageUrl,
        });
        if (lastPrintedTimerRef.current) clearTimeout(lastPrintedTimerRef.current);
        lastPrintedTimerRef.current = setTimeout(() => setLastPrintedInfo(null), 15000);

        clearGarment();
      }
    } catch (error) {
      feedback.triggerError(t('printer.statusUpdateError'), getUserMessage(error));
    }
  }, [garmentData, selectedLocation?.id, agentConnected, printViaAgent, markPrintedMutation, markDualSideMutation, applyDualSideMark, feedback, clearGarment, t, blockedSides, refuseFileTypeBlocked, stationFormat, fileTypeText]);

  // Wrapper: if side already printed, show confirmation first; otherwise print directly.
  // BLU-1336: the already-printed branch is guarded before the reprint confirm
  // opens; the print branch is guarded inside handlePrint. Only a blocked side
  // goes async, so an unblocked reprint confirm opens exactly as today.
  const handleDualPrint = useCallback((side: 'front' | 'back') => {
    const alreadyDone = side === 'front' ? frontPrintedAt : backPrintedAt;
    if (alreadyDone) {
      const openReprintConfirm = () => {
        setConfirmView({ side, notDone: requiredSidesPrinted });
        setReprintConfirmSide(side);
      };
      if (!blockedSides.includes(side)) {
        openReprintConfirm();
        return;
      }
      void refuseFileTypeBlocked(side).then((refused) => {
        if (!refused) openReprintConfirm();
      });
    } else {
      void handlePrint(side);
    }
  }, [frontPrintedAt, backPrintedAt, requiredSidesPrinted, handlePrint, blockedSides, refuseFileTypeBlocked]);

  // BLU-1287 B2: the front-only Print, its already-printed Reprint and the
  // printer_print hotkey. With no per-side time this is exactly today's path
  // (handlePrint -> markPrinted). A garment that already carries a per-side
  // time (half-printed before its SKU was confirmed Front only) must complete
  // through the per-side writer, because markPrinted refuses any garment with
  // a per-side time: handleDualPrint('front') prints the front and marks it via
  // markDualSidePrinted, or opens the reprint confirm when the front is stamped.
  const handleFrontOnlyPrint = useCallback(() => {
    if (frontPrintedAt || backPrintedAt) {
      handleDualPrint('front');
    } else {
      void handlePrint();
    }
  }, [frontPrintedAt, backPrintedAt, handleDualPrint, handlePrint]);

  // BLU-1287 B2: "Mark as printed" (the main button, and in the reprint
  // confirm, shown only while requiredSidesPrinted). Nothing is printed, so no
  // agent call and no agent-connection guard; it re-stamps that side's time to
  // now and lets the DAO complete the garment. The dialog stays open (its
  // buttons disabled) until the mark settles, then closes before any error
  // modal opens (one modal at a time, PRODUCTION_DESIGN.md §6.1). The ref
  // drops a second tap that lands before `isPending` re-renders the buttons.
  const markingRef = useRef(false);
  const handleMarkAsPrinted = useCallback(async (side: 'front' | 'back') => {
    if (!garmentData || !selectedLocation?.id || markingRef.current) return;
    markingRef.current = true;
    try {
      const result = await markDualSideMutation.mutateAsync({
        garmentUnitId: garmentData.garmentUnit.id,
        locationId: selectedLocation.id,
        side,
        requiredFront: garmentData.hasFront,
        requiredBack: garmentData.hasBack,
      });
      setReprintConfirmSide(null);
      applyDualSideMark(result);
    } catch (error) {
      setReprintConfirmSide(null);
      feedback.triggerError(t('printer.statusUpdateError'), getUserMessage(error));
    } finally {
      markingRef.current = false;
    }
  }, [garmentData, selectedLocation?.id, markDualSideMutation, applyDualSideMark, feedback, t]);

  // BLU-435 LOW-2 + post-impl PI-FC-HB / PI-S1: Retry from the Not-ready state
  // with 500ms debounce. useState (NOT useRef) so the button's `disabled` prop
  // actually re-renders during the cool-down — a ref mutation doesn't trigger
  // React, so a useRef-backed disabled would lie visually (button stays active
  // while clicks no-op). The early-return guard is belt-and-braces.
  const [retryDebounced, setRetryDebounced] = useState(false);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleRetryScan = useCallback(() => {
    if (retryDebounced) return;
    setRetryDebounced(true);
    if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    retryTimeoutRef.current = setTimeout(() => setRetryDebounced(false), 500);
    hasScannedRef.current = false;
    clearGarment();
  }, [clearGarment, retryDebounced]);
  // Clear the debounce timer on unmount so a setState on a dead component is impossible.
  useEffect(() => () => {
    if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
  }, []);

  // Handle QR scan
  const handleScan = useCallback((qrCode: string) => {
    hasScannedRef.current = true;
    // BLU-233 + 2026-08-08 DB-trust fix: NOT decided from the scanned label's
    // `m` field here — it can go stale once a unit's print_technology is
    // corrected after the label was printed. Every scan proceeds to the
    // normal lookup below; getGarmentForPrint's own DTF_PRODUCT guard reads
    // the DB and is authoritative — its rejection (parsed in the lookupError
    // effect) is the ONLY signal allowed to bounce to the Film-Print station.
    // (lastScannedGarmentIdRef is recorded inside lookupGarmentById itself.)
    const qrType = detectQRType(qrCode);

    if (qrType === 'bin' || qrType === 'shelf') {
      feedback.triggerError(
        t('qr.wrongType', { type: qrType }),
        t('qr.wrongType', { type: qrType }),
      );
      return;
    }

    if (qrType === 'unknown') {
      feedback.triggerError(
        t('qr.unrecognised'),
        t('qr.unrecognised'),
      );
      return;
    }

    lookupGarmentById(qrCode);
  }, [lookupGarmentById, feedback, t]);

  // Loading a garment from the resolved-issues retry feed is a scan-equivalent
  // action, so mark hasScannedRef first: without it, a failed lookup off a panel
  // tap is swallowed by the error effect's `if (!hasScannedRef.current) return`
  // guard (:257) and the operator gets no modal.
  const handleLoadFromPanel = useCallback((qr: string) => {
    hasScannedRef.current = true;
    lookupGarmentById(qr);
  }, [lookupGarmentById]);

  // BLU-1509 "Print it DTG instead": the safe in-place flip, then the normal
  // lookup of the same shirt (or the not-ready path above).
  const mismatchGarmentId = mismatch.garmentId;
  const mismatchNeedsPretreat = mismatch.needsPretreat;
  const handleSwitchToDtg = useCallback(async (): Promise<PrintTechnologySwitchAttempt> => {
    if (!mismatchGarmentId || !selectedLocation?.id) return { ok: false, errorCode: null };
    const result = await switchToDtg({ garmentUnitId: mismatchGarmentId, locationId: selectedLocation.id });
    if (!result.ok) return { ok: false, errorCode: result.errorCode };
    setFlippedShirt({ garmentId: mismatchGarmentId, needsPretreat: mismatchNeedsPretreat });
    reportedFlipFileRef.current = null;
    closeSwitched();
    hasScannedRef.current = true;
    lookupGarmentById(encodeGarmentQr(mismatchGarmentId));
    return { ok: true };
  }, [mismatchGarmentId, mismatchNeedsPretreat, selectedLocation?.id, switchToDtg, closeSwitched, lookupGarmentById]);

  // Handle issue report submission
  const handleReportIssue = useCallback(async (reason: string, notes?: string) => {
    if (!garmentData || !selectedLocation?.id) return;

    // BLU-651 D1: EVERY print-file reason routes through the dedicated
    // reportPrintFileMissing resolver path — it stamps kind / filename /
    // order_item_id (+ artwork URLs) into the issue metadata, which the
    // upload/resolve flow needs. The generic issues.create below stamped none
    // of that, which is why wrong-data issues filed there could never be
    // resolved by the corrected-file uploader. Falls through to the generic
    // create if the resolver path errors, so the report is never lost.
    // `missing_artwork` keeps its exact pre-BLU-651 call shape (no reason /
    // notes / extra) so its behaviour stays byte-identical.
    try {
      const res = await reportPrintFileMissingMutation.mutateAsync({
        garmentUnitId: garmentData.garmentUnit.id,
        locationId: selectedLocation.id,
        ...(reason !== 'missing_artwork'
          ? {
              reason,
              notes,
              // Context bag the generic path used to record — preserved via
              // the raise path's `extra` metadata merge.
              extra: {
                failure_code: lastPrintFailureRef.current?.code,
                correlation_id: lastPrintFailureRef.current?.correlationId,
                failure_stage: lastPrintFailureRef.current?.stage,
                http_status: lastPrintFailureRef.current?.httpStatus,
                product_name: garmentData.garmentUnit.productName,
                variant_title: garmentData.garmentUnit.variantName,
                image_url: garmentData.garmentUnit.imageUrl,
                color: garmentData.garmentUnit.color,
                size: garmentData.garmentUnit.size,
              },
            }
          : {}),
      });
      // A dual product with no print-data resolves to print_data_needed — the
      // fix is the front/back print-data form on the Issues page, not an upload.
      toast.success(
        res.reason === 'print_data_needed'
          ? t('printer.printDataNeededReported')
          : res.reason === 'file_missing'
            ? t('printer.fileMissingReported')
            : t('printer.issueReported'),
      );
      setIssueDialogOpen(false);
      clearGarment();
      return;
    } catch (error) {
      // Phase 92: a supply garment whose blank could not be read is a retry,
      // not a report to file here. The generic issues.create below refuses the
      // rules stamp, so the issue would read as the client's own. The dialog
      // stays open so the operator can send the report again.
      if (isSupplySourceUnreadable(error)) {
        toast.error(t('printer.supplyCheckFailedRetry'));
        return;
      }
      // Fall through to the generic issues.create so the report is never lost.
      toast.warning(t('printer.fileMissingFallback'));
    }

    try {
      await reportIssueMutation.mutateAsync({
        location_id: selectedLocation.id,
        issue_type: 'PRINT_FILE_ISSUE',
        garment_unit_id: garmentData.garmentUnit.id,
        prod_order_id: garmentData.order.id,
        blocked_sku: garmentData.garmentUnit.shopifySku ?? garmentData.garmentUnit.sku,
        notes,
        metadata: {
          reason,
          // Structured print-failure context from the last failed attempt, so a
          // dev opening this issue sees the exact code + can search Sentry/Railway
          // by correlation id — no need to read the operator's local log.
          failure_code: lastPrintFailureRef.current?.code,
          correlation_id: lastPrintFailureRef.current?.correlationId,
          failure_stage: lastPrintFailureRef.current?.stage,
          http_status: lastPrintFailureRef.current?.httpStatus,
          product_name: garmentData.garmentUnit.productName,
          variant_title: garmentData.garmentUnit.variantName,
          image_url: garmentData.garmentUnit.imageUrl,
          color: garmentData.garmentUnit.color,
          size: garmentData.garmentUnit.size,
        },
      });

      toast.success(t('printer.issueReported'));
      setIssueDialogOpen(false);
      clearGarment();
    } catch (error) {
      toast.error(getUserMessage(error));
    }
  }, [garmentData, selectedLocation?.id, reportIssueMutation, reportPrintFileMissingMutation, clearGarment, t]);

  // Handle garment issue (contamination / defect / wrong item)
  const handleGarmentIssue = useCallback(async (reason: GarmentIssueReason, notes?: string) => {
    if (!garmentData || !selectedLocation?.id) return;

    try {
      await reportGarmentIssueMutation.mutateAsync({
        garmentUnitId: garmentData.garmentUnit.id,
        locationId: selectedLocation.id,
        reason,
        notes,
      });

      toast.success(t('printer.garmentIssue.reported'));
      setGarmentIssueDialogOpen(false);
      clearGarment();
    } catch (error) {
      toast.error(getUserMessage(error));
    }
  }, [garmentData, selectedLocation?.id, reportGarmentIssueMutation, clearGarment, t]);

  const hasActiveGarment = !!garmentData;
  const isMarkingPrinted = markPrintedMutation.isPending || markDualSideMutation.isPending;
  const confirmSideLabel = confirmView.side === 'back' ? t('printer.reprintConfirmBack') : t('printer.reprintConfirmFront');

  // Configurable hotkeys via useHotkeyEngine (BLU-289).
  // BLU-435: routed per sideState so back-only / front-only / dual / notReady
  // each bind only the keys that match their rendered button set. printer_print
  // (the operator's muscle-memory Enter) is REBOUND to handleDualPrint('back')
  // in back-only state so a dropped keystroke never becomes a silent no-op.
  const { hotkeyMap } = useHotkeySettings();
  const hotkeyHandlers = useMemo(() => {
    if (!hasActiveGarment) return {};
    const handlers: Partial<Record<HotkeyAction, () => void>> = {
      printer_issue: () => setIssueDialogOpen(true),
      printer_wrong: () => setGarmentIssueDialogOpen(true),
      printer_clear: () => clearGarment(),
    };
    if (requiredSidesPrinted) {
      // BLU-1287: every required side is printed, not marked done. The print
      // binding (combo or hold) is Mark as printed, like the main button; no
      // file is sent. Reprint stays a click (and the f / b keys where they
      // exist). A press while the mark saves is dropped by the pause below
      // and by handleMarkAsPrinted's ref guard.
      handlers.printer_print = () => void handleMarkAsPrinted(markSide);
      if (sideState === 'dual') handlers.printer_front = () => handleDualPrint('front');
      if (sideState === 'dual' || sideState === 'backOnly') handlers.printer_back = () => handleDualPrint('back');
      return handlers;
    }
    if (sideState === 'dual') {
      handlers.printer_front = () => handleDualPrint('front');
      handlers.printer_back = () => handleDualPrint('back');
    } else if (sideState === 'frontOnly') {
      handlers.printer_print = () => handleFrontOnlyPrint();
    } else if (sideState === 'backOnly') {
      // Anti-dropped-Enter: bind both printer_back AND printer_print to back.
      handlers.printer_back = () => handleDualPrint('back');
      handlers.printer_print = () => handleDualPrint('back');
    }
    // notReady: only printer_clear (already bound above) stays live.
    // No print operation is valid until files appear.
    return handlers;
  }, [hasActiveGarment, sideState, requiredSidesPrinted, markSide, handleMarkAsPrinted, handleFrontOnlyPrint, handleDualPrint, clearGarment]);

  useHotkeyEngine(hotkeyMap, hotkeyHandlers, {
    // `isPrinting` guards against a hotkey re-firing mid-print and dispatching
    // a second concurrent handlePrint (the on-screen Print buttons are already
    // disabled while printing; the hotkey wasn't) → double print / double mark.
    // `isMarkingPrinted` (BLU-1287): usePrintAgent clears isPrinting in its
    // finally BEFORE the page's mark mutation resolves, so without it a
    // repeated printer_print during the mark prints and marks twice.
    paused: feedback.isModalOpen || mismatch.open || isPrinting || isMarkingPrinted || issueDialogOpen || garmentIssueDialogOpen || testFileOpen || !!reprintConfirmSide || lightbox.open,
  });

  // Location guard
  if (!selectedLocation) {
    return (
      <div className="container mx-auto px-6 py-6 max-w-7xl animate-enter">
        <Card>
          <CardContent className="py-16 text-center">
            <Printer className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg text-muted-foreground">
              {t('printer.selectLocation')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const shopifyStoreName = organization?.shopify_domain?.replace('.myshopify.com', '') ?? null;
  const isBusy = isLookingUp || isPrinting || isMarkingPrinted || feedback.isModalOpen || mismatch.open;

  return (
    <div className="container mx-auto px-6 py-6 max-w-7xl space-y-6 animate-enter flex flex-col min-h-[calc(100dvh-4.5rem)]">
      {/* Retry feed — freshly-resolved print-file issues the printer can reload
          and reprint. FIRST child of the stack so, when stuck at top:0, it carries
          no `space-y-6` top margin (a mid-stack sticky leaves a ~24px band above
          it that content scrolls through — DESIGN.md §3.1). Renders nothing when
          empty; force-collapses to the strip while a garment is active so it never
          covers the print task. Mirrors the sticky-bottom action bar (last child). */}
      <ResolvedIssuesPanel
        locationId={selectedLocation?.id ?? null}
        onLoadGarment={handleLoadFromPanel}
        hasActiveGarment={hasActiveGarment}
        hidden={hasActiveGarment || isLookingUp}
      />

      {/* ARIA live region */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {isLookingUp && t('lookingUp')}
        {garmentData && `Garment found: ${garmentData.garmentUnit.productName}`}
        {lookupError && `Error: ${lookupError}`}
      </div>

      {/* Page header. flex-wrap so the zh badge never pushes the row sideways. */}
      <div className="flex flex-wrap items-center gap-3">
        {hasActiveGarment && (
          <Button
            variant="ghost"
            onClick={clearGarment}
            className="shrink-0 gap-1.5"
            aria-label={t('backTo', { station: t('printer.title') })}
          >
            <ArrowLeft className="h-5 w-5" />
            <kbd className="text-[10px] font-normal text-muted-foreground border bg-muted px-1.5 py-0.5 rounded">{formatBindingLabel(hotkeyMap.printer_clear)}</kbd>
          </Button>
        )}
        <div className="p-2 rounded-lg bg-primary/10">
          <Printer className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold">{t('printer.title')}</h1>
          <p className="text-muted-foreground">
            {t('printer.description')}
          </p>
        </div>
        {/* Right-aligned cluster: the printer badge and Test a Print File wrap
            together, so the badge never pushes the button off the row. */}
        <div className="ml-auto flex flex-wrap items-center justify-end gap-3">
          {/* BLU-1336: which printer and file type this PC prints with, from the
              local agent's /status declaration. Non-interactive; nothing while
              probing or offline (the offline card covers that). */}
          {showPrinterFileType && (
            <span
              data-testid="printer-file-type-badge"
              className={cn(
                'inline-flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-base font-medium',
                printerFormat
                  ? 'bg-sky-50 text-sky-900 border-sky-200 dark:bg-sky-950/40 dark:text-sky-100 dark:border-sky-800'
                  : 'bg-muted text-muted-foreground border-border',
              )}
            >
              <Printer className="h-4 w-4" aria-hidden="true" />
              {printerFormat
                ? t('printer.fileType.badge', {
                  printer: PRINTER_FILE_TYPE[printerFormat].printer,
                  type: PRINTER_FILE_TYPE[printerFormat].type,
                })
                : t('printer.fileType.badgeNotSet')}
            </span>
          )}
          {/* Always-available "Test a Print File" debug downloader. Right-aligned so
              it never competes with the scan-first flow or the sticky print bar. */}
          <Button
            variant="outline"
            className="shrink-0 gap-2 h-11 px-4"
            onClick={() => setTestFileOpen(true)}
            aria-label={t('printer.testFile.button')}
          >
            <FlaskConical className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">{t('printer.testFile.button')}</span>
          </Button>
        </div>
      </div>

      <ConnectionStatusBanner heartbeat={heartbeat} />

      {/* Scrollable content area */}
      <div className="flex-1 space-y-6">
        {/* ACTIVE state: garment card */}
        {hasActiveGarment && (
          <>
            <PrintGarmentCard
              garment={garmentData.garmentUnit}
              order={garmentData.order}
              sideState={sideState!}
              isDualPrint={garmentData.isDualPrint}
              shopifyStoreName={shopifyStoreName}
              previewFacts={previewFacts!}
              onOpenPreview={handleOpenPreview}
              needsPretreatAfterSwitch={showFlipPretreat}
            />

            {/* Issue buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 gap-2"
                onClick={() => setIssueDialogOpen(true)}
              >
                <AlertTriangle className="h-4 w-4" />
                {t('printer.reportPrintIssue')}
                <kbd className="ml-auto text-[10px] font-normal text-muted-foreground border bg-muted px-1.5 py-0.5 rounded">{formatBindingLabel(hotkeyMap.printer_issue)}</kbd>
              </Button>
              <Button
                variant="outline"
                className="flex-1 gap-2 border-orange-200 text-orange-700 hover:bg-orange-50 dark:border-orange-800 dark:text-orange-400 dark:hover:bg-orange-950/30"
                onClick={() => setGarmentIssueDialogOpen(true)}
              >
                <AlertTriangle className="h-4 w-4" />
                {t('printer.garmentIssue.button')}
                <kbd className="ml-auto text-[10px] font-normal text-muted-foreground border bg-muted px-1.5 py-0.5 rounded">{formatBindingLabel(hotkeyMap.printer_wrong)}</kbd>
              </Button>
            </div>

            {/* Hidden scanner for USB input. keepFocusWhenBusy: stays focusable
                while printing so a mid-print scan is buffered, not dropped onto
                the page body / URL (FRONTEND-4N). */}
            <div className="sr-only">
              <QRScannerInput
                onScan={handleScan}
                disabled={isBusy}
                isLoading={isLookingUp}
                keepFocusWhenBusy
              />
            </div>
          </>
        )}

        {/* Loading state: shown while looking up a garment (replaces idle content) */}
        {!hasActiveGarment && isLookingUp && (
          <>
            <Card>
              <CardContent className="py-16 text-center">
                <Loader2 className="h-12 w-12 text-muted-foreground mx-auto mb-4 animate-spin" />
                <p className="text-lg text-muted-foreground">{t('lookingUp')}</p>
              </CardContent>
            </Card>
            {/* FRONTEND-4N: keep a mounted [data-scanner-input] during lookup so
                the global auto-focus always has a target — previously this state
                rendered NO scanner and a scan landed on the URL bar as
                /production/printerTHP:/… → 404. Scans here are key-ahead-buffered
                and processed once the lookup settles. */}
            <div className="sr-only">
              <QRScannerInput
                onScan={handleScan}
                disabled={isBusy}
                isLoading={isLookingUp}
                keepFocusWhenBusy
              />
            </div>
          </>
        )}

        {/* IDLE state: visible scanner */}
        {!hasActiveGarment && !isLookingUp && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">{t('scanGarment')}</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <QRScannerInput
                onScan={handleScan}
                disabled={isBusy}
                isLoading={isLookingUp}
              />
            </CardContent>
          </Card>
        )}

        {/* Sent to Printer confirmation */}
        {lastPrintedInfo && !hasActiveGarment && (
          <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-xl animate-enter">
            {lastPrintedInfo.imageUrl && (
              <ZoomImg
                src={lastPrintedInfo.imageUrl}
                alt=""
                className="w-10 h-10 rounded-md object-cover shrink-0 border"
              />
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Printer className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0" />
                <span className="text-sm font-semibold text-green-700 dark:text-green-300">{t('printer.sentToPrinter')}</span>
              </div>
              <p className="text-sm text-green-800 dark:text-green-200 truncate">
                {lastPrintedInfo.productName}
                {lastPrintedInfo.size && `, ${lastPrintedInfo.size}`}
                {lastPrintedInfo.color && ` / ${lastPrintedInfo.color}`}
              </p>
            </div>
          </div>
        )}

        {/* Recently Printed (idle state, hidden during loading) */}
        {!hasActiveGarment && !isLookingUp && recentlyPrinted?.items && recentlyPrinted.items.length > 0 && (
          <Card>
            <CardHeader className="pb-2 pt-4 px-4">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <CardTitle className="text-sm font-medium text-muted-foreground">{t('recentlyPrinted')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="px-0 pb-2 pt-0">
              <div className="divide-y">
                {recentlyPrinted.items.map((item) => {
                  const isCurrentlyPrinting = lastPrintedInfo?.id === item.id;
                  return (
                    <button
                      key={item.id}
                      className={cn(
                        'w-full px-4 py-2.5 flex items-center gap-3 hover:bg-muted/50 transition-colors text-left',
                        isCurrentlyPrinting && 'bg-green-50/50 dark:bg-green-950/20',
                      )}
                      onClick={() => lookupGarmentById(encodeGarmentQr(item.id))}
                    >
                      {isCurrentlyPrinting && (
                        <span className="relative flex h-2.5 w-2.5 shrink-0">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
                        </span>
                      )}
                      <span className="font-medium text-sm min-w-0 truncate flex-1">{item.productName}</span>
                      {isCurrentlyPrinting && (
                        <Badge className="bg-green-100 text-green-700 hover:bg-green-100 dark:bg-green-900/50 dark:text-green-300 text-xs shrink-0">
                          Printing
                        </Badge>
                      )}
                      {item.printSide && item.printSide !== 'both' && (
                        <Badge variant="outline" className="text-xs shrink-0 capitalize">
                          {item.printSide}
                        </Badge>
                      )}
                      {item.size && (
                        <Badge variant="secondary" className="text-xs shrink-0">
                          {item.size}
                        </Badge>
                      )}
                      <span className="text-sm text-muted-foreground shrink-0">{formatOrderNumber(item.orderName)}</span>
                      <span className="text-xs text-muted-foreground shrink-0 w-14 text-right">
                        {isCurrentlyPrinting ? t('now') : formatTimeAgo(item.printedAt)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Agent offline warning */}
        {!agentProbing && !agentConnected && (
          <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
            <WifiOff className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                {t('printer.agentNotDetected')}
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-300">
                {t('printer.agentNotDetectedDesc')}
              </p>
            </div>
          </div>
        )}

        {/* Ready indicator (no active print confirmation) */}
        {!lastPrintedInfo && !hasActiveGarment && !isLookingUp && (
          <div className="sr-only" role="status">{t('printer.readyForNext')}</div>
        )}
      </div>

      {/* Sticky action buttons — BLU-435 4-state matrix keyed off sideState */}
      {hasActiveGarment && sideState && (
        <div className="sticky bottom-0 z-10 bg-background pb-4 pt-4 border-t">
          {/* BLU-1336: a side still to print that this printer cannot print (its
              file proven missing, the other printer's present). Red = stop;
              role="status" is polite, the scan-feedback modal owns the
              assertive alert. When nothing here can print on a Kora, the main
              Print is hidden and the pill carries the status at text-lg. */}
          {fileTypePillShown && stationFormat && (
            <div
              id={fileTypePillId}
              role="status"
              data-testid="printer-file-type-pill"
              className="mb-3 flex items-center gap-2 px-3 py-2 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg"
            >
              <AlertCircle className="h-5 w-5 text-red-700 dark:text-red-300 shrink-0" aria-hidden="true" />
              <p
                className={cn(
                  'font-medium text-red-900 dark:text-red-100',
                  hideBlockedPrint && !showFileTypeRetry ? 'text-lg' : 'text-base',
                )}
              >
                {fileTypeText(blockCopy(stationFormat), blockedToPrint).body}
              </p>
            </div>
          )}
          {/* Already printed — front-only (existing single-print reprint affordance) */}
          {garmentData.alreadyPrinted && sideState === 'frontOnly' && (
            <div className="space-y-2">
              <div className="flex items-center justify-center gap-2 p-2 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                <p className="text-sm font-semibold text-green-700 dark:text-green-300">
                  {t('printer.alreadyPrintedScanNext')}
                </p>
              </div>
              {agentConnected && (
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full h-12 text-base"
                  onClick={handleFrontOnlyPrint}
                  disabled={isPrinting}
                >
                  {isPrinting ? t('printer.reprinting') : t('printer.reprint')}
                </Button>
              )}
            </div>
          )}

          {/* Already printed — back-only (new reprint Back affordance) */}
          {garmentData.alreadyPrinted && sideState === 'backOnly' && (
            <div className="space-y-2">
              <div className="flex items-center justify-center gap-2 p-2 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                <p className="text-sm font-semibold text-green-700 dark:text-green-300">
                  {t('printer.alreadyPrintedScanNext')}
                </p>
              </div>
              {agentConnected && (
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full h-12 text-base"
                  onClick={() => handleDualPrint('back')}
                  disabled={isPrinting || isMarkingPrinted}
                >
                  {isPrinting ? t('printer.reprinting') : t('printer.reprintBack')}
                </Button>
              )}
            </div>
          )}

          {/* Already printed — dual (BOTH sides done; can reprint either side).
              Before this branch existed, an operator re-scanning a fully-
              printed dual garment got NO buttons — the dual block below is
              gated on `!alreadyPrinted`, and the two alreadyPrinted blocks
              above are single-side only. The reprint flow re-uses
              `handleDualPrint` which routes through the existing reprint
              confirmation dialog (since both `frontPrintedAt` /
              `backPrintedAt` are non-null). */}
          {garmentData.alreadyPrinted && sideState === 'dual' && (
            <div className="space-y-2">
              <div className="flex items-center justify-center gap-2 p-2 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                <p className="text-sm font-semibold text-green-700 dark:text-green-300">
                  {t('printer.alreadyPrintedScanNext')}
                </p>
              </div>
              {agentConnected && (
                <div className="flex gap-4">
                  <Button
                    size="lg"
                    variant="outline"
                    className="flex-1 h-12 text-base"
                    onClick={() => handleDualPrint('front')}
                    disabled={isPrinting || isMarkingPrinted}
                  >
                    <CheckCircle className="h-5 w-5 mr-2" />
                    {isPrinting ? t('printer.reprinting') : t('printer.reprintFront')}
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="flex-1 h-12 text-base"
                    onClick={() => handleDualPrint('back')}
                    disabled={isPrinting || isMarkingPrinted}
                  >
                    <CheckCircle className="h-5 w-5 mr-2" />
                    {isPrinting ? t('printer.reprinting') : t('printer.reprintBack')}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* BLU-1287: every required side printed, not marked done. One
              status line (amber: waiting on the operator), Mark as printed as
              the main button (no file is sent), Reprint as secondary: it opens
              the confirm, which says the garment is not finished. */}
          {requiredSidesPrinted && (
            <div className="space-y-3">
              <div
                role="status"
                className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg"
              >
                <AlertCircle className="h-5 w-5 text-amber-700 dark:text-amber-300 shrink-0" aria-hidden="true" />
                <p className="text-lg font-medium text-amber-900 dark:text-amber-100">
                  {garmentData.hasFront && garmentData.hasBack
                    ? t('printer.printedNotDoneBoth')
                    : garmentData.hasFront ? t('printer.printedNotDoneFront') : t('printer.printedNotDoneBack')}
                </p>
              </div>
              <Button
                size="lg"
                className="w-full h-16 text-xl font-semibold gap-2"
                onClick={() => void handleMarkAsPrinted(markSide)}
                disabled={isPrinting || isMarkingPrinted}
              >
                <CheckCircle className="h-5 w-5" aria-hidden="true" />
                {isMarkingPrinted ? t('printer.updating') : t('printer.markAsPrinted')}
                {/* The print binding marks in this state (BLU-1287). */}
                <kbd className="ml-2 text-xs font-normal opacity-60 bg-white/20 px-1.5 py-0.5 rounded">{formatBindingLabel(hotkeyMap.printer_print)}</kbd>
              </Button>
              {agentConnected && (
                <div className="flex gap-4">
                  {garmentData.hasFront && (
                    <Button
                      variant="outline"
                      className="flex-1 h-12 text-base"
                      onClick={sideState === 'frontOnly' ? handleFrontOnlyPrint : () => handleDualPrint('front')}
                      disabled={isPrinting || isMarkingPrinted}
                    >
                      {t('printer.reprintFront')}
                      {/* Front-only has no reprint key here: the print key marks. */}
                      {sideState === 'dual' && (
                        <kbd className="ml-2 text-xs font-normal opacity-60 bg-muted px-1.5 py-0.5 rounded">
                          {formatBindingLabel(hotkeyMap.printer_front)}
                        </kbd>
                      )}
                    </Button>
                  )}
                  {garmentData.hasBack && (
                    <Button
                      variant="outline"
                      className="flex-1 h-12 text-base"
                      onClick={() => handleDualPrint('back')}
                      disabled={isPrinting || isMarkingPrinted}
                    >
                      {t('printer.reprintBack')}
                      <kbd className="ml-2 text-xs font-normal opacity-60 bg-muted px-1.5 py-0.5 rounded">
                        {formatBindingLabel(hotkeyMap.printer_back)}
                      </kbd>
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Dual — Front / Back buttons (unchanged from v1.0; sideState='dual' implies isDualPrint).
              BLU-1336: a blocked side's Print is disabled; when every side
              still to print is blocked it is not rendered at all. */}
          {!garmentData.alreadyPrinted && !requiredSidesPrinted && sideState === 'dual' && !(hideBlockedPrint && frontBlocked && backBlocked) && (
            <div className="space-y-3">
              <div className="flex gap-4">
                {!(hideBlockedPrint && frontBlocked) && (
                  <Button
                    size="lg"
                    className={cn(
                      'flex-1 h-16 text-xl font-semibold',
                      frontPrintedAt && 'bg-green-600 hover:bg-green-700 border-green-600 text-white'
                    )}
                    variant={frontPrintedAt ? 'outline' : 'default'}
                    onClick={() => handleDualPrint('front')}
                    disabled={isPrinting || isMarkingPrinted || frontBlocked}
                    aria-describedby={frontBlocked ? fileTypePillId : undefined}
                  >
                    {frontPrintedAt ? (
                      <>
                        <CheckCircle className="h-5 w-5 mr-2" />
                        {t('printer.reprintFront')}
                        <kbd className="ml-2 text-xs font-normal opacity-60 bg-white/20 px-1.5 py-0.5 rounded">{formatBindingLabel(hotkeyMap.printer_front)}</kbd>
                      </>
                    ) : isPrinting ? t('printer.printing') : (
                      <>
                        {t('printer.printFront')}
                        <kbd className="ml-2 text-xs font-normal opacity-60 bg-white/20 px-1.5 py-0.5 rounded">{formatBindingLabel(hotkeyMap.printer_front)}</kbd>
                      </>
                    )}
                  </Button>
                )}
                {!(hideBlockedPrint && backBlocked) && (
                  <Button
                    size="lg"
                    className={cn(
                      'flex-1 h-16 text-xl font-semibold',
                      backPrintedAt && 'bg-green-600 hover:bg-green-700 border-green-600 text-white'
                    )}
                    variant={backPrintedAt ? 'outline' : 'default'}
                    onClick={() => handleDualPrint('back')}
                    disabled={isPrinting || isMarkingPrinted || backBlocked}
                    aria-describedby={backBlocked ? fileTypePillId : undefined}
                  >
                    {backPrintedAt ? (
                      <>
                        <CheckCircle className="h-5 w-5 mr-2" />
                        {t('printer.reprintBack')}
                        <kbd className="ml-2 text-xs font-normal opacity-60 bg-white/20 px-1.5 py-0.5 rounded">{formatBindingLabel(hotkeyMap.printer_back)}</kbd>
                      </>
                    ) : isPrinting ? t('printer.printing') : (
                      <>
                        {t('printer.printBack')}
                        <kbd className="ml-2 text-xs font-normal opacity-60 bg-white/20 px-1.5 py-0.5 rounded">{formatBindingLabel(hotkeyMap.printer_back)}</kbd>
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Front-only — single Print button. A stamped front (half-printed
              garment) is the not-marked-done block above (BLU-1287). */}
          {!garmentData.alreadyPrinted && !requiredSidesPrinted && sideState === 'frontOnly' && !(hideBlockedPrint && frontBlocked) && (
            <Button
              size="lg"
              className="w-full h-16 text-xl font-semibold"
              onClick={handleFrontOnlyPrint}
              disabled={isPrinting || isMarkingPrinted}
            >
              {isPrinting ? t('printer.printing') : isMarkingPrinted ? t('printer.updating') : t('printer.print')}
              <kbd className="ml-2 text-xs font-normal opacity-60 bg-white/20 px-1.5 py-0.5 rounded">{formatBindingLabel(hotkeyMap.printer_print)}</kbd>
            </Button>
          )}

          {/* Back-only — single Print Back button (new; routes through handleDualPrint) */}
          {!garmentData.alreadyPrinted && !requiredSidesPrinted && sideState === 'backOnly' && !(hideBlockedPrint && backBlocked) && (
            <Button
              size="lg"
              className="w-full h-16 text-xl font-semibold"
              onClick={() => handleDualPrint('back')}
              disabled={isPrinting || isMarkingPrinted}
            >
              {isPrinting ? t('printer.printing') : isMarkingPrinted ? t('printer.updating') : t('printer.printBack')}
              <kbd className="ml-2 text-xs font-normal opacity-60 bg-white/20 px-1.5 py-0.5 rounded">{formatBindingLabel(hotkeyMap.printer_back)}</kbd>
            </Button>
          )}

          {/* BLU-1336: a Brother whose .arx4 is not there yet for every side
              still to print (usually RIP lag). The main action is the same
              Retry Scan the not-ready state uses, never Report Print Issue. */}
          {showFileTypeRetry && (
            <Button
              size="lg"
              className={cn('w-full h-16 text-xl font-semibold gap-2', (frontPrintedAt || backPrintedAt) && 'mt-3')}
              onClick={handleRetryScan}
              disabled={retryDebounced}
            >
              <RefreshCw className="h-5 w-5" aria-hidden="true" />
              {t('printer.retryScan')}
            </Button>
          )}

          {/* Not-ready — small status pill + single hero Retry CTA (MED-14) */}
          {sideState === 'notReady' && (
            <div className="space-y-3">
              <div
                role="status"
                className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg"
              >
                <AlertCircle className="h-4 w-4 text-amber-700 dark:text-amber-300 shrink-0" aria-hidden="true" />
                <p className="text-sm font-medium text-amber-900 dark:text-amber-100">{t('printer.filesNotReadyBanner')}</p>
              </div>
              <Button
                size="lg"
                className="w-full h-16 text-xl font-semibold gap-2"
                onClick={handleRetryScan}
                disabled={retryDebounced}
              >
                <RefreshCw className="h-5 w-5" aria-hidden="true" />
                {t('printer.retryScan')}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Garment image lightbox — the product previews on the card, in the
          order they are rendered, ridden by the arrows and the arrow keys.
          Closing hands the scanner back (PRODUCTION_DESIGN §6.2), and both
          pause lists above hold while it is open. */}
      <ProductionImageLightbox
        open={lightbox.open}
        onOpenChange={lightbox.setOpen}
        images={lightbox.images}
        index={lightbox.index}
        onIndexChange={lightbox.setIndex}
        copy={{
          title: t('printer.preview.lightboxTitle'),
          description: t('printer.preview.lightboxDescription'),
          previousLabel: t('preview.previousImage'),
          nextLabel: t('preview.nextImage'),
        }}
      />

      {/* Scan error modal */}
      <ScanErrorModal {...feedback.modalProps} />

      {/* Superseded-sticker dialog (amber; one-tap reprint of the live replacement) */}
      <SupersededStickerDialog
        {...feedback.supersededProps}
        locationId={selectedLocation?.id ?? null}
        currentStage="Printing"
      />

      {/* BLU-1509: a DTF shirt scanned here (go to the film station or print
          it DTG instead). Blocking: both pause lists hold while it is open. */}
      <PrintTechnologyMismatchDialog {...mismatch.dialogProps} onSwitch={handleSwitchToDtg} />

      {/* Reprint confirmation dialog. BLU-1287: while every required side is
          printed but the garment is not done, it says so and offers Mark as
          printed; while a mark saves, every button is disabled and it cannot
          be dismissed. Its words come from confirmView, frozen at open. */}
      <AlertDialog
        open={!!reprintConfirmSide}
        onOpenChange={(open) => {
          if (!open && !isMarkingPrinted) setReprintConfirmSide(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(confirmView.notDone ? 'printer.notDoneConfirmTitle' : 'printer.reprintConfirm', { side: confirmSideLabel })}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base">
              {t(confirmView.notDone ? 'printer.notDoneConfirmDesc' : 'printer.reprintConfirmDesc', { side: confirmSideLabel })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-12 text-base" disabled={isMarkingPrinted}>{t('common:cancel')}</AlertDialogCancel>
            {confirmView.notDone && (
              <Button
                variant="outline"
                className="h-12 text-base"
                disabled={isMarkingPrinted}
                onClick={() => {
                  if (reprintConfirmSide) void handleMarkAsPrinted(reprintConfirmSide);
                }}
              >
                {isMarkingPrinted ? t('printer.updating') : t('printer.markAsPrinted')}
              </Button>
            )}
            <AlertDialogAction
              className="h-12 text-base"
              disabled={isMarkingPrinted}
              onClick={() => {
                if (reprintConfirmSide) void handlePrint(reprintConfirmSide);
                setReprintConfirmSide(null);
              }}
            >
              {t('printer.reprint')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Issue report dialog */}
      <PrintIssueDialog
        open={issueDialogOpen}
        onOpenChange={setIssueDialogOpen}
        garment={garmentData ? {
          id: garmentData.garmentUnit.id,
          sku: garmentData.garmentUnit.sku,
          productName: garmentData.garmentUnit.productName,
          size: garmentData.garmentUnit.size,
          color: garmentData.garmentUnit.color,
        } : null}
        onSubmit={handleReportIssue}
        isPending={reportIssueMutation.isPending || reportPrintFileMissingMutation.isPending}
        reasons={printFileReasons}
        reasonsLoading={printFileReasonsQuery.isLoading}
      />

      {/* Garment issue dialog */}
      <GarmentIssueDialog
        open={garmentIssueDialogOpen}
        onOpenChange={setGarmentIssueDialogOpen}
        garment={garmentData ? {
          sku: garmentData.garmentUnit.sku,
          productName: garmentData.garmentUnit.productName,
          size: garmentData.garmentUnit.size,
          color: garmentData.garmentUnit.color,
        } : null}
        onConfirm={handleGarmentIssue}
        isPending={reportGarmentIssueMutation.isPending}
      />

      {/* Test a Print File — resolve + download a SKU's ripped file (no print, no DB write) */}
      <TestPrintFileDialog
        open={testFileOpen}
        onOpenChange={setTestFileOpen}
        locationId={selectedLocation?.id ?? null}
      />
    </div>
  );
}

// ============================================================================
// PrintGarmentCard — Inline component for print-specific garment display
// ============================================================================

interface PrintGarmentCardProps {
  garment: {
    id: string;
    sku: string;
    productName: string;
    variantName: string | null;
    size: string | null;
    color: string | null;
    status: string;
    itemIndex: number;
    totalInOrder: number;
    isRedo: boolean;
    redoReason: string | null;
    requiresPretreat: boolean;
  };
  order: {
    orderName: string;
    customerName: string | null;
  };
  sideState: SideState;
  isDualPrint: boolean;
  shopifyStoreName: string | null;
  /**
   * The garment's display preview facts. DISPLAY ONLY: the print decision is
   * made from `sideState` / `isDualPrint` above and the printable file is
   * resolved server-side. Nothing in here can reach a printer, the bridge, the
   * manifest or the agent, and no tile can add or remove a printed side.
   */
  previewFacts: ProductionPreviewFacts;
  /** Opens the page's garment-wide lightbox at the tapped tile's image. */
  onOpenPreview: (kind: ProductionPreviewKind, sourceIndex: number) => void;
  /**
   * BLU-1509: just switched DTF to DTG here and needs pretreat: the banner,
   * and the PRE-TREAT badge (`requires_pretreat` is never set on a garment).
   */
  needsPretreatAfterSwitch?: boolean;
}

function PrintGarmentCard({
  garment,
  order,
  sideState,
  isDualPrint,
  shopifyStoreName,
  previewFacts,
  onOpenPreview,
  needsPretreatAfterSwitch = false,
}: PrintGarmentCardProps) {
  const { t } = useTranslation('production');
  const displayName = cleanGarmentDisplayName(garment.productName, garment.color, garment.size);
  const showPretreatBadge = garment.requiresPretreat || needsPretreatAfterSwitch;

  return (
    <Card className={cn(
      'transition-all',
      garment.isRedo && 'border-l-4 border-l-amber-500'
    )}>
      <CardContent className="pt-6">
        {/* BLU-435: HEAD-verified side indicator banner — MUST be first child of CardContent */}
        <PrintSideIndicator sideState={sideState} />

        {/* BLU-1509: the page plays its one buzz. */}
        {needsPretreatAfterSwitch && (
          <PretreatWarning role="status" data-testid="print-pretreat-after-switch" className="mb-4" />
        )}

        {/* Post-impl PI-FC-MD: the in-card "Already printed" banner was removed
            here. The PrintSideIndicator carries the side state, and each
            sticky-bottom branch (frontOnly / backOnly / dual) renders its own
            already-printed affordance with the reprint CTA. Stacking three
            green/sky/amber banners at the top of the card created a wall of
            colour before the operator could see the SKU. */}

        {/* Order header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold">{formatOrderNumber(order.orderName)}</span>
              {shopifyStoreName && (
                <a
                  href={`https://admin.shopify.com/store/${shopifyStoreName}/orders?query=${order.orderName}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground"
                  aria-label={`View order ${order.orderName} in Shopify admin`}
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </div>
            {order.customerName && (
              <p className="text-sm text-muted-foreground">{order.customerName}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {garment.isRedo && (
              <Badge variant="destructive" className="flex items-center gap-1">
                <RefreshCw className="h-3 w-3" />
                REDO
              </Badge>
            )}
            {/* Phase 50.1: was an untranslated `DUAL` literal. Now the shared
                FilmSidesBadge (same component the press station uses), at `sm`
                — the full-width PrintSideIndicator banner above already states
                the side state at station scale, so this is the compact
                confirmation in the header, not a second shout.
                `isBackOnly` is deliberately NOT passed: back-only is the
                banner's own sky state here, and an amber twin beside it would
                collide with this station's amber = "not ready" meaning. */}
            <FilmSidesBadge sides={{ isDual: isDualPrint, isBackOnly: false }} size="sm" />
            {showPretreatBadge ? (
              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 dark:bg-amber-900/50 dark:text-amber-200">
                PRE-TREAT
              </Badge>
            ) : (
              <Badge variant="secondary" className="text-muted-foreground">
                NO PT
              </Badge>
            )}
            <Badge variant="outline">
              {t('itemOf', { index: garment.itemIndex, total: garment.totalInOrder })}
            </Badge>
          </div>
        </div>

        {/* Product details.

            PRODUCT PREVIEWS, then identity. What the operator needs to see is
            the design on the garment: one combined tile for a custom, equal
            Front and Back tiles for a standard. Display only. The strip fills
            this column and takes its height from that width rather than from
            the image, so an image that arrives late or falls back cannot move
            the print controls under the operator's hand mid-scan.
            See PrintStationPreviews.tsx for the full rule. */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
          <div className="order-1 min-w-0 lg:flex-1">
            <PrintStationPreviews
              facts={previewFacts}
              subject={displayName}
              onOpen={onOpenPreview}
            />
          </div>

          <div className="order-2 flex min-w-0 flex-col gap-3 lg:w-72 lg:shrink-0">
            <div className="min-w-0 space-y-1">
              <p className="text-lg font-semibold leading-tight">{displayName}</p>
              <p className="font-mono text-sm text-muted-foreground break-all">{garment.sku}</p>
            </div>
            <div className="flex items-center gap-3">
              {garment.size && (
                <Badge variant="secondary" className="text-2xl font-bold px-4 py-2">
                  {garment.size}
                </Badge>
              )}
              {garment.color && (
                <span className="text-base font-medium">{garment.color}</span>
              )}
            </div>
          </div>
        </div>

        {/* Redo reason */}
        {garment.isRedo && garment.redoReason && (
          <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">{t('garmentCard.redoReason')}</p>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mt-1">
              {/* BLU-1412: getGarmentForPrint refuses a DTF garment from the DB,
                  so a switched replacement on this card was switched to DTG. */}
              {garment.redoReason === PRINT_TECHNOLOGY_SWITCH_REASON
                ? t('printTechSwitch.reason', { tech: 'DTG' })
                : garment.redoReason === CLIENT_QC_REDO_REASON
                  ? t('clientQcRedo.reason')
                  : garment.redoReason.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// PrintSideIndicator — Banner-style 4-state HEAD-verified indicator (BLU-435)
// ============================================================================
//
// Renders as the FIRST child of PrintGarmentCard's CardContent. Bleeds to card
// edges via negative margins to read as a banner across the top.
//
// Palette differentiation per v1.1 review + BLU-435 follow-ups (1 + 2):
// - Dual:      brand-primary tint + Layers icon (complex-but-default state)
// - FrontOnly: emerald tint + Shirt icon (semantic ready-to-print cue;
//              promoted from muted in follow-up #1 after operator feedback
//              that the muted treatment read as "sad / easy to miss")
// - BackOnly:  sky tint + Shirt icon (matching visual weight with FrontOnly
//              per follow-up #2 — same icon family signals "single-side
//              print"; palette + copy carry the front/back distinction)
// - NotReady:  amber + pulsing AlertCircle + role="alert"/aria-live="assertive"
//
// Type scale per PRODUCTION_DESIGN.md §4.2: text-2xl for ready states,
// text-3xl for Not-ready (must interrupt). Audio twin fires from PrintStationPage.

interface PrintSideIndicatorProps {
  sideState: SideState;
}

function PrintSideIndicator({ sideState }: PrintSideIndicatorProps) {
  const { t } = useTranslation('production');

  // Post-impl PI-FC-HD + PI-FC-MA: the dual palette was originally
  // `text-primary on bg-primary/10` (light) + `text-primary-foreground on
  // bg-primary/20` (dark). Self-on-self tint reads as "unfinished" in light
  // mode, and `text-primary-foreground` is the token designed to sit on
  // SOLID `bg-primary` — on the /20 tint it falls below the AAA contrast
  // target. Fix: neutral high-contrast text, brand accent moves to the
  // icon + border. Keeps brand identity without the contrast trap.
  const config = {
    dual: {
      palette: 'bg-primary/10 text-foreground border-primary/30 dark:bg-primary/20 dark:text-foreground dark:border-primary/40',
      icon: <Layers className="h-7 w-7 shrink-0 text-primary dark:text-primary" aria-hidden="true" />,
      copyKey: 'printer.sideIndicator.dual',
    },
    frontOnly: {
      // BLU-435 follow-up: original muted palette read as "sad / easy to miss"
      // in operator testing. Promoted to emerald (semantic ready-to-print cue
      // per PRODUCTION_DESIGN.md §4.1 "emerald = ready / completed") with a
      // <Shirt /> icon so the indicator carries the same visual weight as the
      // Dual / Back-only / Not-ready banners. Four-palette semantic grid
      // preserved (Dual=primary, Front-only=emerald, Back-only=sky,
      // Not-ready=amber). Supersedes the deferred design decision in BLU-476.
      palette: 'bg-emerald-50 text-emerald-900 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-100 dark:border-emerald-800',
      icon: <Shirt className="h-7 w-7 shrink-0 text-emerald-700 dark:text-emerald-300" aria-hidden="true" />,
      copyKey: 'printer.sideIndicator.frontOnly',
    },
    backOnly: {
      // BLU-435 follow-up #2: match visual weight with Front-only. Same Shirt
      // icon family (signals "single-side print"), sky-700 colour matches the
      // banner's sky-50 wash. Differentiation from Front-only is carried by
      // the palette (sky vs emerald) + copy ("Back only" vs "Front only"),
      // not by icon shape — operators read palette + text faster at
      // standing distance than they parse rotated/mirrored icons.
      palette: 'bg-sky-50 text-sky-900 border-sky-200 dark:bg-sky-950/40 dark:text-sky-100 dark:border-sky-800',
      icon: <Shirt className="h-7 w-7 shrink-0 text-sky-700 dark:text-sky-300" aria-hidden="true" />,
      copyKey: 'printer.sideIndicator.backOnly',
    },
    notReady: {
      palette: 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950/40 dark:text-amber-100 dark:border-amber-800',
      icon: <AlertCircle className="h-7 w-7 shrink-0 motion-safe:animate-pulse motion-reduce:animate-none" aria-hidden="true" />,
      copyKey: 'printer.sideIndicator.notReady',
    },
  }[sideState];

  const isNotReady = sideState === 'notReady';
  const textClasses = isNotReady
    ? 'text-3xl font-semibold leading-tight'
    : 'text-2xl font-semibold leading-tight';

  return (
    <div
      role={isNotReady ? 'alert' : 'status'}
      aria-live={isNotReady ? 'assertive' : 'polite'}
      className={cn(
        // Banner bleed: negative margins cancel CardContent pt-6 + px-6 so the
        // indicator fills the top edge of the card.
        'flex items-center gap-3 px-6 py-4 mx-[-1.5rem] mt-[-1.5rem] mb-4',
        'rounded-t-lg border-b',
        // Post-impl PI-FC-HE: sticky banner for notReady. Operator-critical
        // alert MUST stay visible while they scroll the card body to read the
        // SKU/size — on narrow tablet viewports the action bar could otherwise
        // hide the original banner. Other states stay non-sticky (their info
        // is conveyed by the buttons + palette, not by the banner being
        // continuously visible).
        isNotReady && 'sticky top-0 z-20',
        config.palette,
      )}
    >
      {config.icon}
      <p className={textClasses}>{t(config.copyKey)}</p>
    </div>
  );
}
